import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { CalendarRange, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const authFetch = async (path, opts = {}) => {
  const res = await fetch(`${API}${path}`, { ...opts, headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}`, 'Content-Type': 'application/json' } });
  if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || `Request failed (${res.status})`); }
  return res.json();
};
const weeks = (a, b) => Math.round((new Date(b) - new Date(a)) / (7 * 86400000) * 10) / 10;

export default function TermDatesPage() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [terms, setTerms] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setData(null);
    authFetch(`/api/student-attendance/term-dates?year=${year}`).then((d) => { setData(d); setTerms(d.terms); }).catch((e) => toast.error(e.message));
  }, [year]);

  const update = (i, k, v) => setTerms((t) => t.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const save = async () => {
    setSaving(true);
    try {
      const r = await authFetch('/api/student-attendance/term-dates', { method: 'PUT', body: JSON.stringify({ academic_year: year, terms }) });
      setTerms(r.terms); setData((d) => ({ ...d, customised: true }));
      toast.success(`Term dates for ${year} saved`);
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="term-dates-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><CalendarRange className="h-6 w-6 text-primary" /> Term Dates</h1>
        <p className="text-sm text-muted-foreground mt-1">These dates decide which days count as school days on the Roll Call calendar, which term teachers can edit, and when the 3pm missed roll call reminders are sent. The same dates apply to every school.</p>
      </div>
      <div className="flex items-center gap-3">
        <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="h-10 border rounded-md px-3 bg-background text-sm" data-testid="term-year-select">
          {[year - 1, year, year + 1].filter((v, i, a) => a.indexOf(v) === i).map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        {data && (data.customised ? <Badge className="bg-emerald-600" data-testid="term-customised-badge">Custom dates saved</Badge> : <Badge variant="outline" data-testid="term-default-badge">Using default dates</Badge>)}
      </div>
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">School year {year}</CardTitle></CardHeader>
        <CardContent>
          {!data ? <Loader2 className="h-5 w-5 animate-spin" /> : (
            <div className="space-y-3">
              {terms.map((t, i) => (
                <div key={t.term} className="grid grid-cols-1 sm:grid-cols-[120px_1fr_1fr_90px] gap-3 items-center" data-testid={`term-row-${i + 1}`}>
                  <p className="font-medium">{t.term}</p>
                  <label className="text-xs text-muted-foreground">Start<Input type="date" value={t.start} onChange={(e) => update(i, 'start', e.target.value)} disabled={!data.can_edit} data-testid={`term-start-${i + 1}`} /></label>
                  <label className="text-xs text-muted-foreground">End<Input type="date" value={t.end} onChange={(e) => update(i, 'end', e.target.value)} disabled={!data.can_edit} data-testid={`term-end-${i + 1}`} /></label>
                  <p className="text-xs text-muted-foreground sm:text-right">{t.start && t.end && new Date(t.end) >= new Date(t.start) ? `${weeks(t.start, t.end)} wks` : <span className="text-red-600">invalid</span>}</p>
                </div>
              ))}
              {data.can_edit ? (
                <div className="flex justify-end pt-2"><Button onClick={save} disabled={saving} className="gap-2" data-testid="term-dates-save-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save term dates</Button></div>
              ) : <p className="text-xs text-muted-foreground">Only the admin (Roy Tari) can change these dates.</p>}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
